/**
 * Product Forecast Chart
 *
 * Displays forecast demand breakdown by product (mushroom strain).
 * Shows predicted volume for each product type.
 * Supports configurable time periods via period/offset props.
 */

import { useState, useEffect } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts'
import { getForecastByPeriod } from '../../services/forecastService'

// Product colours - gradient from primary
const PRODUCT_COLOURS = [
  '#F54A00', // Primary orange
  '#FF6B2C',
  '#FF8C5A',
  '#FFAD88',
  '#009689', // Teal
  '#00B8A9',
  '#6366F1', // Indigo
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#9CA3AF'  // Gray fallback
]

// Custom tooltip with solid background
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <p className="text-sm font-medium mb-2">{data.strain}</p>
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-4 text-xs">
            <span className="text-muted-foreground">Predicted</span>
            <span className="font-medium">{data.predicted.toFixed(1)}kg</span>
          </div>
          <div className="flex items-center justify-between gap-4 text-xs">
            <span className="text-muted-foreground">Historical</span>
            <span className="font-medium">{data.historical.toFixed(1)}kg</span>
          </div>
          {data.historical > 0 && (
            <div className="flex items-center justify-between gap-4 text-xs">
              <span className="text-muted-foreground">Change</span>
              <span className={`font-medium ${data.predicted >= data.historical ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                {data.predicted >= data.historical ? '+' : ''}
                {((data.predicted - data.historical) / data.historical * 100).toFixed(0)}%
              </span>
            </div>
          )}
        </div>
      </div>
    )
  }
  return null
}

export default function ProductForecastChart({
  period = '2weeks',
  offset = 0,
  loading: externalLoading
}) {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        const forecast = await getForecastByPeriod(period, offset)

        // Use byProduct data directly (already sorted by predicted volume)
        setData(forecast.byProduct || [])
      } catch (error) {
        console.error('Error loading product forecast:', error)
        setData([])
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [period, offset])

  const isLoading = externalLoading !== undefined ? externalLoading : loading

  if (isLoading) {
    return (
      <div className="reports-chart-container">
        <div className="reports-loading">
          Loading product data...
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="reports-chart-container">
        <div className="reports-loading">
          No product forecast data available
        </div>
      </div>
    )
  }

  return (
    <div className="reports-chart-container">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis
            dataKey="strain"
            axisLine={false}
            tickLine={false}
            tick={{
              fontSize: 11,
              fill: 'var(--muted-foreground)',
              fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{
              fontSize: 12,
              fill: 'var(--muted-foreground)',
              fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
            label={{
              value: 'Predicted Volume (kg)',
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle', fontSize: '12px', fill: 'var(--muted-foreground)' }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            dataKey="predicted"
            name="Predicted Volume"
            radius={[4, 4, 0, 0]}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={PRODUCT_COLOURS[index % PRODUCT_COLOURS.length]}
                opacity={0.85}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
